import { useState } from 'react';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useT, useLang } from '@/lib/i18n';
import { motion } from 'framer-motion';
import { 
  Phone, Mail, MapPin, Clock, Send, 
  CheckCircle, AlertCircle, Loader2 
} from 'lucide-react';
import { z } from 'zod';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { getBreadcrumbJsonLd, BASE_URL, COMPANY } from '@/lib/seo-constants';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { FileDropzone } from '@/components/ui/file-dropzone';
import { useToast } from '@/hooks/use-toast';
import { useFileUpload } from '@/hooks/useFileUpload';
import { supabase } from '@/integrations/supabase/client';
import larsImg from '@/assets/lars-zimmermann.webp';

type Translator = <T,>(de: T, en: T) => T;

const createContactSchema = (t: Translator) =>
  z.object({
    name: z
      .string()
      .trim()
      .min(2, t('Name muss mindestens 2 Zeichen haben', 'Name must be at least 2 characters'))
      .max(100),
    company: z.string().trim().max(100).optional(),
    email: z
      .string()
      .trim()
      .email(t('Ungültige E-Mail-Adresse', 'Invalid email address'))
      .max(255),
    phone: z.string().trim().max(50).optional(),
    subject: z
      .string()
      .trim()
      .min(5, t('Betreff muss mindestens 5 Zeichen haben', 'Subject must be at least 5 characters'))
      .max(200),
    message: z
      .string()
      .trim()
      .min(
        20,
        t('Nachricht muss mindestens 20 Zeichen haben', 'Message must be at least 20 characters'),
      )
      .max(2000),
    privacy: z.literal(true, {
      errorMap: () => ({
        message: t(
          'Bitte stimmen Sie der Datenschutzerklärung und den AGB zu',
          'Please accept the privacy policy and terms & conditions',
        ),
      }),
    }),
  });

interface ContactFormState {
  name: string;
  company: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  privacy: boolean;
}

export default function KontaktPage() {
  const t = useT();
  const lang = useLang();
  const { toast } = useToast();
  const { uploadFiles, isUploading, progress: uploadProgress } = useFileUpload();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [formData, setFormData] = useState<ContactFormState>({
    name: '',
    company: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    privacy: false,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormState, string>>>({});

  const isBusy = isSubmitting || isUploading;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof ContactFormState]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      createContactSchema(t).parse(formData);

      // 1. Upload attachments
      let attachmentPaths: string[] = [];
      if (attachments.length > 0) {
        attachmentPaths = await uploadFiles(attachments, 'contact');
      }

      // 2. Save to DB
      const { error: dbError } = await supabase.from('contact_leads').insert({
        name: formData.name,
        company: formData.company || null,
        email: formData.email,
        phone: formData.phone || null,
        subject: formData.subject,
        message: formData.message,
        privacy: formData.privacy,
        attachment_paths: attachmentPaths.length > 0 ? attachmentPaths : null,
      });

      if (dbError) throw dbError;

      // 3. Email notification (fire & forget)
      supabase.functions.invoke('send-lead-notification', {
        body: { ...formData, attachment_paths: attachmentPaths, lang: t('de', 'en') },
      }).catch(console.error);

      toast({
        title: t('Nachricht gesendet!', 'Message sent!'),
        description: t('Vielen Dank für Ihre Anfrage. Wir melden uns schnellstmöglich bei Ihnen.', 'Thank you for your enquiry. We will get back to you as soon as possible.'),
      });

      setFormData({ name: '', company: '', email: '', phone: '', subject: '', message: '', privacy: false });
      setAttachments([]);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Partial<Record<keyof ContactFormState, string>> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as keyof ContactFormState] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast({
          title: t('Fehler', 'Error'),
          description: error instanceof Error ? error.message : t('Bitte versuchen Sie es später erneut.', 'Please try again later.'),
          variant: 'destructive',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <SEOHead
        title={t('Kontakt – Anfrage & Angebot', 'Contact – Enquiry & quote')}
        description={t('Kontaktieren Sie FERNAU Präzisionstechnik in Darmstadt. Anfrage für CNC-Drehen, Fräsen, Baugruppen. Tel: 06150 184973-0.', 'Contact FERNAU Präzisionstechnik in Darmstadt, Germany. Enquiry for CNC turning, milling and assemblies. Phone: +49 6150 184973-0.')}
        path="/kontakt"
        jsonLd={[
          getBreadcrumbJsonLd([{ name: t('Kontakt', 'Contact'), path: '/kontakt' }], lang),
          {
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            name: t(
              'Kontakt – FERNAU Präzisionstechnik GmbH',
              'Contact – FERNAU Präzisionstechnik GmbH',
            ),
            url: `${BASE_URL}/kontakt`,
            mainEntity: {
              '@type': 'Organization',
              '@id': `${BASE_URL}/#organization`,
              contactPoint: {
                '@type': 'ContactPoint',
                telephone: COMPANY.phone,
                contactType: 'customer service',
                availableLanguage: 'German',
              },
            },
          },
        ]}
      />
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="container mx-auto px-4 sm:px-6 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.span
                className="text-primary font-mono text-sm uppercase tracking-wider"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {t('Kontakt aufnehmen', 'Get in touch')}
              </motion.span>
              <motion.h1
                className="mt-4 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                {t(<>Sprechen Sie <span className="text-primary">mit uns</span></>, <>Talk <span className="text-primary">to us</span></>)}
              </motion.h1>
              <motion.p
                className="mt-6 text-xl text-muted-foreground max-w-2xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                {t(
                  'Kompetente und fachgerechte Beratung – unverbindlich und persönlich. Wir freuen uns auf Ihre Anfrage.',
                  'Competent, expert advice – non-binding and personal. We look forward to your enquiry.'
                )}
              </motion.p>
            </div>
            <motion.div
              className="hidden lg:flex justify-center"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <div className="w-64 h-64 rounded-2xl overflow-hidden border-2 border-primary/20 shadow-[var(--shadow-glow)]">
                <img
                  src={larsImg}
                  alt="Lars Zimmermann – Ihr Ansprechpartner"
                  className="w-full h-full object-cover image-protected"
                  draggable={false}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div
              className="lg:col-span-1 space-y-8"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="card-industrial p-6">
                <h3 className="text-lg font-semibold mb-6">{t('Kontaktdaten', 'Contact details')}</h3>
                <div className="space-y-6">
                  <ContactInfoItem icon={MapPin} label={t('Adresse', 'Address')}>
                    <address className="mt-1 text-sm text-muted-foreground not-italic">
                      FERNAU Präzisionstechnik GmbH<br />
                      Merianstraße 5a<br />
                      64291 Darmstadt{t('', ', Germany')}
                    </address>
                  </ContactInfoItem>
                  <ContactInfoItem icon={Phone} label={t('Telefon', 'Phone')}>
                    <a href="tel:+4961501849730" className="mt-1 text-sm text-muted-foreground hover:text-primary transition-colors font-mono">
                      {t('06150 184973-0', '+49 6150 184973-0')}
                    </a>
                  </ContactInfoItem>
                  <ContactInfoItem icon={Mail} label={t('E-Mail', 'Email')}>
                    <a href="mailto:info@fernau-gmbh.de" className="mt-1 text-sm text-muted-foreground hover:text-primary transition-colors">
                      info@fernau-gmbh.de
                    </a>
                  </ContactInfoItem>
                  <ContactInfoItem icon={Clock} label={t('Bürozeiten', 'Office hours')}>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {t('Mo-Do: 7:30 - 16:00 Uhr', 'Mon-Thu: 7:30 - 16:00')}<br />
                      {t('Fr: 7:30 - 12:30 Uhr', 'Fri: 7:30 - 12:30')}
                    </div>
                  </ContactInfoItem>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="card-industrial p-8">
                <h3 className="text-xl font-semibold mb-6">{t('Anfrage senden', 'Send an enquiry')}</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField label={t('Name', 'Name')} required error={errors.name}>
                      <Input name="name" value={formData.name} onChange={handleChange} placeholder={t('Ihr Name', 'Your name')} className={errors.name ? 'border-destructive' : ''} />
                    </FormField>
                    <FormField label={t('Firma', 'Company')}>
                      <Input name="company" value={formData.company} onChange={handleChange} placeholder={t('Ihre Firma', 'Your company')} />
                    </FormField>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField label={t('E-Mail', 'Email')} required error={errors.email}>
                      <Input type="email" name="email" value={formData.email} onChange={handleChange} placeholder={t('ihre@email.de', 'your@email.com')} className={errors.email ? 'border-destructive' : ''} />
                    </FormField>
                    <FormField label={t('Telefon', 'Phone')}>
                      <Input name="phone" value={formData.phone} onChange={handleChange} placeholder={t('Ihre Telefonnummer', 'Your phone number')} />
                    </FormField>
                  </div>

                  <FormField label={t('Betreff', 'Subject')} required error={errors.subject}>
                    <Input name="subject" value={formData.subject} onChange={handleChange} placeholder={t('Worum geht es?', 'What is it about?')} className={errors.subject ? 'border-destructive' : ''} />
                  </FormField>

                  <FormField label={t('Nachricht', 'Message')} required error={errors.message}>
                    <Textarea name="message" value={formData.message} onChange={handleChange} placeholder={t('Beschreiben Sie Ihr Anliegen...', 'Describe your request...')} rows={6} className={errors.message ? 'border-destructive' : ''} />
                  </FormField>

                  {/* File Upload */}
                  <div>
                    <label className="block text-sm font-medium mb-2">{t('Anhänge', 'Attachments')}</label>
                    <FileDropzone files={attachments} onFilesChange={setAttachments} disabled={isBusy} uploadProgress={uploadProgress} />
                  </div>

                  {/* DSGVO Checkbox */}
                  <div className="space-y-2">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="privacy"
                        checked={formData.privacy}
                        onCheckedChange={(checked) => {
                          setFormData((prev) => ({ ...prev, privacy: checked as boolean }));
                          if (errors.privacy) setErrors((prev) => ({ ...prev, privacy: undefined }));
                        }}
                        className={errors.privacy ? 'border-destructive' : ''}
                      />
                      <label htmlFor="privacy" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                        {t('Ich habe die ', 'I have read the ')}
                        <LocalizedLink to="/datenschutz" className="text-primary hover:underline" target="_blank">
                          {t('Datenschutzerklärung', 'privacy policy')}
                        </LocalizedLink>{' '}
                        {t('und die ', 'and the ')}
                        <LocalizedLink to="/agb" className="text-primary hover:underline" target="_blank">
                          {t('AGB', 'terms and conditions')}
                        </LocalizedLink>{' '}
                        {t('gelesen und stimme der Verarbeitung meiner Daten zur Bearbeitung meiner Anfrage zu. ', 'and consent to the processing of my data for handling my enquiry. ')}
                        <span className="text-primary">*</span>
                      </label>
                    </div>
                    {errors.privacy && (
                      <p className="text-sm text-destructive flex items-center gap-1 ml-7">
                        <AlertCircle className="w-3 h-3" />
                        {errors.privacy}
                      </p>
                    )}
                  </div>

                  <Button type="submit" variant="hero" size="lg" disabled={isBusy} className="w-full md:w-auto">
                    {isBusy ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {isUploading
                          ? t('Dateien werden hochgeladen...', 'Uploading files...')
                          : t('Wird gesendet...', 'Sending...')}
                      </>
                    ) : (
                      <>
                        {t('Nachricht senden', 'Send message')}
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Delivery Info */}
      <section className="py-16 bg-card">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-bold">{t('Lieferung & Service', 'Delivery & service')}</h2>
            <p className="mt-4 text-muted-foreground">
              {t(
                'Unser Sitz ist Darmstadt. Regionale Kunden im Rhein-Main-Gebiet beliefern wir direkt per eigenem Fahrdienst. Deutschlandweit versenden wir per DHL, DPD oder Spedition, üblicherweise in 24 bis 48 Stunden. In die gesamte EU liefern wir in zwei bis fünf Werktagen inklusive EUR.1-Zertifikat und Werkszeugnis. Für Kunden in der Schweiz, Großbritannien, den USA und im asiatischen Raum arbeiten wir mit etablierten Logistikpartnern und Luftfracht in fünf bis zehn Tagen. Internationale Bestandskunden sind bei FERNAU seit Jahren die Regel, nicht die Ausnahme.',
                'We are based in Darmstadt, Germany. Regional customers in the Rhine-Main area receive parts by our own delivery service. Nationwide shipping across Germany goes out via DHL, DPD or freight forwarder within 24 to 48 hours. EU-wide delivery arrives in two to five business days including EUR.1 certificate and mill test report. For customers in Switzerland, the UK, the US and Asia we work with established logistics partners and air freight for five to ten day delivery. International customers have been a regular part of our order book for years, not the exception.',
              )}
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-4 gap-y-3 text-sm text-primary">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                <span>{t('Regionaler Direktlieferservice', 'Regional direct delivery')}</span>
              </div>
              <span className="hidden sm:block text-muted-foreground">•</span>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                <span>{t('Bundesweiter Versand', 'Nationwide shipping')}</span>
              </div>
              <span className="hidden sm:block text-muted-foreground">•</span>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                <span>{t('EU-Versand 2–5 Werktage', 'EU shipping 2–5 business days')}</span>
              </div>
              <span className="hidden sm:block text-muted-foreground">•</span>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                <span>{t('Weltweit per Luftfracht', 'Worldwide by air freight')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

/** Small helper components to reduce repetition */
function ContactInfoItem({ icon: Icon, label, children }: { icon: React.ElementType; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div>
        <div className="font-medium">{label}</div>
        {children}
      </div>
    </div>
  );
}

function FormField({ label, required, error, children }: { label: string; required?: boolean; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-2">
        {label} {required && <span className="text-primary">*</span>}
      </label>
      {children}
      {error && (
        <p className="mt-1 text-sm text-destructive flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </p>
      )}
    </div>
  );
}
